import { collection, query, where, getDocs, addDoc, updateDoc, Timestamp, doc, orderBy, writeBatch, getDoc, setDoc, limit } from 'firebase/firestore';
import { db, auth, testNotificationDev } from '../firebase';
import { resolveUserIdentifiers } from './userIdentification';
import { getMessaging, getToken } from 'firebase/messaging';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { formatInTimeZone } from 'date-fns-tz';

export const ReminderQueueTester = {
  // View all reminders for a user (updated for new schema)
  async viewQueue(userId) {
    const q = query(
      collection(db, 'users', userId, 'reminderQueue'),
      where('queueStatus', '==', 'queued'),
      where('archived', '==', false),
      orderBy('scheduledFor', 'desc')
    );
    
    const snap = await getDocs(q);
    return snap.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      scheduledFor: doc.data().scheduledFor?.toDate(),
      createdAt: doc.data().createdAt?.toDate(),
      sentAt: doc.data().sentAt?.toDate(),
      completedAt: doc.data().completedAt?.toDate()
    }));
  },

  // Add a test reminder (updated for new schema and nextReminderDate logic)
  async addTestReminder(userId, discipleId, discipleName, minutesFromNow = 5) {
    try {
      // Always resolve appID for the user
      const userDoc = await getDoc(doc(db, 'users', userId));
      const appID = userDoc.data()?.appID || userId;
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', appID);
      const mentorDetailsSnap = await getDoc(mentorDetailsRef);
      let frequency_at_creation = 7; // default
      let nextReminderDate = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); // default: 1 week from now
      let preferredTime = '09:00';
      if (mentorDetailsSnap.exists()) {
        const settings = mentorDetailsSnap.data().reminderSettings;
        if (settings) {
          if (typeof settings.frequency === 'number') frequency_at_creation = settings.frequency;
          if (settings.nextReminderDate) nextReminderDate = new Date(settings.nextReminderDate);
          if (settings.preferredTime) preferredTime = settings.preferredTime;
        }
      } else {
        // Create mentorDetails doc with default reminderSettings
        await setDoc(mentorDetailsRef, {
          reminderSettings: {
            frequency: frequency_at_creation,
            nextReminderDate: nextReminderDate.toISOString(),
            preferredTime,
            enabled: true,
          }
        });
      }

      // Set the preferred time on the nextReminderDate
      const [hour, minute] = preferredTime.split(':').map(Number);
      nextReminderDate.setHours(hour, minute, 0, 0);

      // Create the reminder
      const reminderData = {
        discipleId,
        discipleName,
        mentorId: userId,
        message: `Test reminder for ${discipleName}`,
        scheduledFor: Timestamp.fromDate(nextReminderDate),
        queueStatus: 'queued',
        reminderStatus: 'scheduled',
        archived: false,
        createdAt: Timestamp.now(),
        frequency_at_creation,
        interval: frequency_at_creation,
        preferredTime,
        attempts: 0,
        messageSent: false,
        notificationsSent: 0,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
      
      const docRef = await addDoc(
        collection(db, 'users', userId, 'reminderQueue'),
        reminderData
      );

      // Calculate and update the next nextReminderDate in mentorDetails
      const newNextReminderDate = new Date(nextReminderDate);
      newNextReminderDate.setDate(newNextReminderDate.getDate() + frequency_at_creation);
      newNextReminderDate.setHours(hour, minute, 0, 0);

      await updateDoc(mentorDetailsRef, {
        'reminderSettings.nextReminderDate': newNextReminderDate.toISOString()
      });

      return docRef.id;
    } catch (error) {
      console.error('Error adding test reminder:', error);
      throw error;
    }
  },

  // Process reminder (updated for new schema)
  async processReminder(userId, reminderId, action = 'completed') {
    const timestamp = Timestamp.now();
    const updates = {
      reminderStatus: action,
      completedAt: timestamp,
      completionReason: action === 'completed' ? 'disciple_completed' : 'mentor_cancelled',
      archived: true,
      archivedAt: timestamp
    };
    
    await updateDoc(doc(db, 'users', userId, 'reminderQueue', reminderId), updates);
  },

  // Cancel all reminders for a user (updated for new schema)
  async cancelAllRemindersForUser(userId) {
    const batch = writeBatch(db);
    const queueRef = collection(db, 'users', userId, 'reminderQueue');
    const activeQuery = query(
      queueRef,
      where('queueStatus', 'in', ['queued', 'sent']),
      where('reminderStatus', 'not-in', ['cancelled', 'completed', 'superseded', 'expired']),
      where('archived', '==', false)
    );

    const snapshot = await getDocs(activeQuery);
    const timestamp = Timestamp.now();
    snapshot.docs.forEach(doc => {
      const reminderRef = doc.ref;
      batch.update(reminderRef, {
        queueStatus: 'completed',
        reminderStatus: 'cancelled',
        completionReason: 'mentor_cancelled',
        cancelledAt: timestamp,
        completedAt: timestamp,
        archived: true,
        archivedAt: timestamp
      });
    });

    await batch.commit();
  },

  // Add proper FCM initialization (unchanged)
  async initializeFCM(userId) {
    try {
      const messaging = getMessaging();
      
      // Check if notifications are supported
      if (!('Notification' in window)) {
        console.warn('This browser does not support notifications');
        return null;
      }

      // First check current permission state
      let permission = Notification.permission;
      
      // Only request if not already granted or denied
      if (permission === 'default') {
        permission = await Notification.requestPermission();
      }
      
      if (permission !== 'granted') {
        console.warn('Notification permission:', permission);
        return null;
      }

      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: process.env.REACT_APP_VAPID_KEY
      });
      
      if (!token) {
        console.warn('No FCM token obtained');
        return null;
      }

      // Store token in Firestore
      await updateDoc(doc(db, 'users', userId), {
        fcmToken: token,
        fcmTokenLastUpdated: Timestamp.now()
      });

      console.log('FCM token stored successfully');
      return token;
    } catch (error) {
      console.error('Error initializing FCM:', error);
      return null;
    }
  },

  // Test notification function
  async testNotification(userId, reminderId, notificationType = 'fcm') {
    try {
      if (!auth.currentUser) {
        throw new Error('User must be authenticated');
      }

      console.log('testNotification called with:', { userId, reminderId, notificationType });
      console.log('Current user:', auth.currentUser.uid);
      
      // Get user doc first
      const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
      const userData = userDoc.data();
      console.log('User doc data:', userData);
      
      // Check if user has a mobile token (preferred for mobile push notifications)
      // Support both new separate fields and old combined field
      const mobileToken = userData?.fcmTokenMobile || (userData?.fcmTokenType === 'mobile' ? userData?.fcmToken : null);
      const webToken = userData?.fcmTokenWeb || (userData?.fcmTokenType === 'web' ? userData?.fcmToken : null);
      
      let fcmToken = null;
      
      // For FCM notifications, prefer mobile token if available, otherwise use web token
      if (notificationType === 'fcm' || notificationType === 'both') {
        if (mobileToken) {
          fcmToken = mobileToken;
          console.log('Using mobile FCM token from Firestore:', fcmToken);
        } else {
          // Try to get web token from browser
          try {
            const messaging = getMessaging();
            const currentToken = await getToken(messaging, {
              vapidKey: process.env.REACT_APP_VAPID_KEY
            });
            if (currentToken) {
              fcmToken = currentToken;
              console.log('FCM token from browser (web):', fcmToken);
              // Update Firestore with web token (but don't overwrite mobile token)
              if (!mobileToken) {
                await updateDoc(doc(db, 'users', auth.currentUser.uid), {
                  fcmToken: currentToken,
                  fcmTokenType: 'web',
                  fcmTokenUpdated: Timestamp.now()
                });
              }
            } else if (webToken) {
              fcmToken = webToken;
              console.log('Using web FCM token from Firestore:', fcmToken);
            }
          } catch (tokenError) {
            console.warn('Could not get token from browser, using Firestore:', tokenError);
            // Fallback to any token in Firestore
            fcmToken = userData?.fcmToken;
          }
        }
      } else {
        // For email-only, we don't need a token
        fcmToken = null;
      }
      
      const email = userDoc.data()?.email;
      console.log('FCM token (final):', fcmToken);
      console.log('Email:', email);

      // Get reminder data for the email
      const reminderDoc = await getDoc(doc(db, 'users', auth.currentUser.uid, 'reminderQueue', reminderId));
      if (!reminderDoc.exists()) {
        console.error('Reminder not found:', reminderId);
        throw new Error('Reminder not found');
      }
      const reminderData = reminderDoc.data();
      console.log('Reminder data:', reminderData);

      const functions = getFunctions();
      
      if (notificationType === 'email' || notificationType === 'both') {
        if (!email) {
          console.error('No email found for user');
          throw new Error('No email found for user');
        }
        console.log('Calling testReminderEmail with:', { to: email, discipleName: reminderData.discipleName });
        const testReminderEmail = httpsCallable(functions, 'testReminderEmail');
        const emailResult = await testReminderEmail({
          to: email,
          discipleName: reminderData.discipleName
        });
        console.log('Email notification result:', emailResult);
      }

      if (notificationType === 'fcm' || notificationType === 'both') {
        if (!fcmToken) {
          console.error('No FCM token found for user');
          throw new Error('No FCM token found for user');
        }
        console.log('Calling testNotificationDev with:', {
          userId: auth.currentUser.uid,
          fcmToken,
          reminderId
        });
        const result = await testNotificationDev({
          userId: auth.currentUser.uid,
          fcmToken,
          reminderId
        });
        console.log('FCM notification result:', result);
        return result.data;
      }

      return { success: true };
    } catch (error) {
      console.error('Error in testNotification:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      throw error;
    }
  },

  async createTestReminder(discipleData) {
    const user = auth.currentUser;
    if (!user) return;

    // Get user's timezone from their profile
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    const userTimezone = userDoc.data()?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Create scheduled time in user's timezone
    let scheduledFor = new Date();
    scheduledFor.setHours(9, 0, 0, 0); // Set to 9am
    
    // Format the date in UTC using formatInTimeZone
    const utcDateString = formatInTimeZone(scheduledFor, userTimezone, "yyyy-MM-dd'T'HH:mm:ss'Z'");
    const utcScheduledFor = new Date(utcDateString);

    const reminderData = {
      discipleId: discipleData.id,
      discipleName: discipleData.name,
      mentorId: user.uid,
      message: `Test reminder for ${discipleData.name}`,
      scheduledFor: Timestamp.fromDate(utcScheduledFor),
      queueStatus: 'queued',
      reminderStatus: 'scheduled',
      archived: false,
      frequency: 7, // weekly
      interval: 7,
      preferredTime: "09:00",
      attempts: 0,
      messageSent: false,
      notificationsSent: 0,
      timezone: userTimezone,
      createdAt: Timestamp.now()
    };

    try {
      const reminderQueueRef = collection(db, 'users', user.uid, 'reminderQueue');
      const docRef = await addDoc(reminderQueueRef, reminderData);
      console.log('Reminder added with ID:', docRef.id);
      return docRef.id;
    } catch (error) {
      console.error('Error creating test reminder:', error);
      throw error;
    }
  },

  // Test cloud function processing
  async testCloudFunctionProcessing(userId, discipleId, discipleName, minutesFromNow = 1) {
    try {
      console.log('🧪 Testing cloud function processing...');
      
      // Create a test reminder that should be processed immediately
      const scheduledFor = new Date(Date.now() + (minutesFromNow * 60 * 1000));
      
      const reminderData = {
        discipleId,
        discipleName,
        mentorId: userId,
        message: `Test reminder for ${discipleName}`,
        scheduledFor: Timestamp.fromDate(scheduledFor),
        queueStatus: 'queued',
        reminderStatus: 'scheduled',
        archived: false,
        notificationMethod: 'fcm',
        frequency: 7,
        interval: 7,
        preferredTime: '09:00',
        createdAt: Timestamp.now(),
        notificationsSent: 0,
        messageSent: false,
        attempts: 0
      };

      const reminderRef = await addDoc(collection(db, 'users', userId, 'reminderQueue'), reminderData);
      console.log('✅ Test reminder created:', reminderRef.id);
      
      // Wait a bit and check if it was processed
      setTimeout(async () => {
        const reminderDoc = await getDoc(doc(db, 'users', userId, 'reminderQueue', reminderRef.id));
        const updatedData = reminderDoc.data();
        console.log('🔍 Reminder status after processing:', {
          id: reminderRef.id,
          queueStatus: updatedData?.queueStatus,
          reminderStatus: updatedData?.reminderStatus,
          sentAt: updatedData?.sentAt,
          notificationsSent: updatedData?.notificationsSent
        });
      }, 10000); // Check after 10 seconds
      
      return reminderRef.id;
    } catch (error) {
      console.error('❌ Error testing cloud function processing:', error);
      throw error;
    }
  },

  // Create an immediate reminder for testing (due within 1 minute)
  async createImmediateTestReminder(userId, discipleId, discipleName) {
    try {
      console.log('🚀 Creating immediate test reminder...');
      
      // Create a reminder that will be processed within 30 seconds
      const scheduledFor = new Date(Date.now() + (30 * 1000)); // 30 seconds from now
      
      const reminderData = {
        discipleId,
        discipleName,
        mentorId: userId,
        message: `Immediate test reminder for ${discipleName}`,
        scheduledFor: Timestamp.fromDate(scheduledFor),
        queueStatus: 'queued',
        reminderStatus: 'scheduled',
        archived: false,
        createdAt: Timestamp.now(),
        // Add fields needed for diagnostic tool
        frequency_at_creation: 7,
        interval: 7,
        preferredTime: '09:00'
      };

      const reminderRef = await addDoc(collection(db, 'users', userId, 'reminderQueue'), reminderData);
      console.log('✅ Immediate test reminder created:', reminderRef.id);
      
      return reminderRef.id;
    } catch (error) {
      console.error('❌ Error creating immediate test reminder:', error);
      throw error;
    }
  },

  // Initialize auto reminder for existing users
  async initializeAutoReminder(userId, discipleId, discipleName, nextReminderDateISO) {
    try {
      console.log('🚀 Initializing auto reminder for existing user...');
      
      // Always resolve appID for the user
      const userDoc = await getDoc(doc(db, 'users', userId));
      const appID = userDoc.data()?.appID || userId;
      
      // Get or create mentorDetails
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', appID);
      const mentorDetailsSnap = await getDoc(mentorDetailsRef);
      
      let frequency = 7; // default
      let preferredTime = '09:00';
      
      if (mentorDetailsSnap.exists()) {
        const settings = mentorDetailsSnap.data().reminderSettings;
        if (settings) {
          frequency = settings.frequency || 7;
          preferredTime = settings.preferredTime || '09:00';
        }
      }
      
      // Parse the provided next reminder date
      const nextReminderDate = new Date(nextReminderDateISO);
      
      // For manual initialization, use the user's selected time
      // Only apply preferred time for auto-generated reminders
      console.log(`🕐 Using user-selected time:`, nextReminderDate.toLocaleString());
      
      // Create the reminder data with all fields needed for diagnostic tool
      const reminderData = {
        discipleId,
        discipleName,
        mentorId: userId,
        message: `Time to connect with ${discipleName}`,
        scheduledFor: Timestamp.fromDate(nextReminderDate),
        queueStatus: 'queued',
        reminderStatus: 'scheduled',
        archived: false,
        createdAt: Timestamp.now(),
        // Add fields needed for diagnostic tool
        frequency_at_creation: frequency,
        interval: frequency,
        preferredTime: preferredTime
      };

      // Create the reminder in the queue
      const reminderRef = await addDoc(collection(db, 'users', userId, 'reminderQueue'), reminderData);
      console.log('✅ Auto reminder created:', reminderRef.id);
      
      // Update mentorDetails with the next reminder date and lastReminderSent
      const updateData = {
        'reminderSettings.nextReminderDate': nextReminderDateISO,
        'reminderSettings.frequency': frequency,
        'reminderSettings.preferredTime': preferredTime,
        'reminderSettings.lastReminderSent': Timestamp.now() // Set current time as lastReminderSent for manual initialization
      };
      
      if (mentorDetailsSnap.exists()) {
        await updateDoc(mentorDetailsRef, updateData);
      } else {
        await setDoc(mentorDetailsRef, {
          appID,
          name: userDoc.data()?.name || userDoc.data()?.displayName || 'Unknown',
          reminderSettings: {
            frequency,
            preferredTime,
            nextReminderDate: nextReminderDateISO,
            lastReminderSent: Timestamp.now(), // Set current time as lastReminderSent for manual initialization
          },
          createdAt: Timestamp.now()
        });
      }
      
      console.log('✅ MentorDetails updated with next reminder date');
      
      return {
        reminderId: reminderRef.id,
        nextReminderDate: nextReminderDateISO,
        frequency,
        preferredTime
      };
    } catch (error) {
      console.error('❌ Error initializing auto reminder:', error);
      throw error;
    }
  },

  // Comprehensive diagnostic function
  async runFullDiagnostic(userId) {
    console.log('🔍 === STARTING FULL REMINDER SYSTEM DIAGNOSTIC ===');
    
    try {
      // 1. Check user data and appID
      console.log('\n📋 1. Checking user data...');
      const userDoc = await getDoc(doc(db, 'users', userId));
      const userData = userDoc.data();
      const appID = userData?.appID || userId;
      
      console.log('User Data:', {
        uid: userId,
        appID: appID,
        email: userData?.email,
        timezone: userData?.timezone,
        fcmToken: userData?.fcmToken ? 'Present' : 'Missing',
        notificationPreferences: userData?.notificationPreferences
      });

      // 2. Get all disciples for this user
      console.log('\n👥 2. Checking disciples...');
      const disciplesQuery = query(
        collection(db, 'disciples'),
        where('mentors', 'array-contains', appID)
      );
      const disciplesSnapshot = await getDocs(disciplesQuery);
      const disciples = disciplesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        createdAt: doc.data().createdAt?.toDate(),
        mentors: doc.data().mentors
      }));
      
      console.log(`Found ${disciples.length} disciples for user ${appID}:`, disciples);

      // 3. Check mentorDetails for each disciple
      console.log('\n⚙️ 3. Checking mentorDetails for each disciple...');
      const mentorDetailsIssues = [];
      
      for (const disciple of disciples) {
        const mentorDetailsRef = doc(db, 'disciples', disciple.id, 'mentorDetails', appID);
        const mentorDetailsDoc = await getDoc(mentorDetailsRef);
        
        if (!mentorDetailsDoc.exists()) {
          mentorDetailsIssues.push({
            discipleId: disciple.id,
            discipleName: disciple.name,
            issue: 'No mentorDetails document found'
          });
          continue;
        }
        
        const mentorData = mentorDetailsDoc.data();
        const reminderSettings = mentorData.reminderSettings;
        
        console.log(`Disciple ${disciple.name}:`, {
          hasReminderSettings: !!reminderSettings,
          frequency: reminderSettings?.frequency,
          preferredTime: reminderSettings?.preferredTime,
          enabled: reminderSettings?.enabled,
          lastMessageDate: reminderSettings?.lastMessageDate,
          nextReminderDate: reminderSettings?.nextReminderDate
        });
        
        if (!reminderSettings || !reminderSettings.enabled) {
          mentorDetailsIssues.push({
            discipleId: disciple.id,
            discipleName: disciple.name,
            issue: 'Reminder settings missing or disabled'
          });
        }
      }

      // 4. Check current reminder queue
      console.log('\n📅 4. Checking current reminder queue...');
      const reminderQueueQuery = query(
        collection(db, 'users', userId, 'reminderQueue'),
        where('archived', '==', false),
        orderBy('scheduledFor', 'desc')
      );
      const reminderQueueSnapshot = await getDocs(reminderQueueQuery);
      const activeReminders = reminderQueueSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        scheduledFor: doc.data().scheduledFor?.toDate(),
        createdAt: doc.data().createdAt?.toDate()
      }));
      
      console.log(`Found ${activeReminders.length} active reminders:`, activeReminders);

      // 5. Check for completed reminders that should have triggered next reminders
      console.log('\n✅ 5. Checking completed reminders...');
      const completedRemindersQuery = query(
        collection(db, 'users', userId, 'reminderQueue'),
        where('reminderStatus', '==', 'completed'),
        where('archived', '==', true),
        orderBy('completedAt', 'desc'),
        limit(10)
      );
      const completedRemindersSnapshot = await getDocs(completedRemindersQuery);
      const recentCompleted = completedRemindersSnapshot.docs.map(doc => ({
        id: doc.id,
        discipleId: doc.data().discipleId,
        completedAt: doc.data().completedAt?.toDate(),
        messageSent: doc.data().messageSent
      }));
      
      console.log(`Found ${recentCompleted.length} recently completed reminders:`, recentCompleted);

      // 6. Check if cloud functions are deployed and working
      console.log('\n☁️ 6. Checking cloud function status...');
      try {
        const functions = getFunctions();
        const testFunction = httpsCallable(functions, 'testNotification');
        console.log('✅ Cloud functions are accessible');
      } catch (error) {
        console.error('❌ Cloud functions error:', error);
      }

      // 7. Generate diagnostic report
      console.log('\n📊 === DIAGNOSTIC REPORT ===');
      console.log('User Status:', {
        hasAppID: !!appID,
        hasFCMToken: !!userData?.fcmToken,
        hasTimezone: !!userData?.timezone
      });
      
      console.log('Disciple Status:', {
        totalDisciples: disciples.length,
        mentorDetailsIssues: mentorDetailsIssues.length,
        issues: mentorDetailsIssues
      });
      
      console.log('Reminder Status:', {
        activeReminders: activeReminders.length,
        recentlyCompleted: recentCompleted.length,
        shouldHaveNextReminders: recentCompleted.filter(r => r.messageSent).length
      });

      // 8. Identify specific problems
      const problems = [];
      
      if (mentorDetailsIssues.length > 0) {
        problems.push('Missing or disabled reminder settings for some disciples');
      }
      
      if (activeReminders.length === 0 && disciples.length > 0) {
        problems.push('No active reminders despite having disciples');
      }
      
      if (recentCompleted.length > 0 && activeReminders.length === 0) {
        problems.push('Completed reminders exist but no next reminders were created');
      }
      
      if (!userData?.fcmToken) {
        problems.push('User has no FCM token for notifications');
      }

      console.log('\n🚨 IDENTIFIED PROBLEMS:', problems);
      
      return {
        userData,
        disciples,
        mentorDetailsIssues,
        activeReminders,
        recentCompleted,
        problems
      };

    } catch (error) {
      console.error('❌ Diagnostic error:', error);
      throw error;
    }
  },

  // Test automatic reminder creation
  async testAutomaticReminderCreation(userId, discipleId) {
    console.log('🧪 Testing automatic reminder creation...');
    
    try {
      // Get user's appID
      const userDoc = await getDoc(doc(db, 'users', userId));
      const appID = userDoc.data()?.appID || userId;
      
      // Get mentorDetails
      const mentorDetailsRef = doc(db, 'disciples', discipleId, 'mentorDetails', appID);
      const mentorDetailsDoc = await getDoc(mentorDetailsRef);
      
      if (!mentorDetailsDoc.exists()) {
        throw new Error('MentorDetails not found');
      }
      
      const mentorData = mentorDetailsDoc.data();
      const reminderSettings = mentorData.reminderSettings;
      
      console.log('Current reminder settings:', reminderSettings);
      
      // Create a test reminder that will be completed immediately
      const testReminderData = {
        discipleId,
        discipleName: 'Test Disciple',
        mentorId: userId,
        message: 'Test reminder for automatic creation',
        scheduledFor: Timestamp.now(),
        queueStatus: 'sent',
        reminderStatus: 'sent',
        archived: false,
        frequency: reminderSettings?.frequency || 7,
        interval: reminderSettings?.frequency || 7,
        preferredTime: reminderSettings?.preferredTime || '09:00',
        createdAt: Timestamp.now(),
        sentAt: Timestamp.now(),
        notificationsSent: 1,
        messageSent: false
      };
      
      const reminderRef = await addDoc(
        collection(db, 'users', userId, 'reminderQueue'),
        testReminderData
      );
      
      console.log('✅ Test reminder created:', reminderRef.id);
      
      // Immediately mark it as completed to trigger next reminder creation
      await updateDoc(doc(db, 'users', userId, 'reminderQueue', reminderRef.id), {
        reminderStatus: 'completed',
        completedAt: Timestamp.now(),
        completionReason: 'test_completion',
        messageSent: true,
        messageSentAt: Timestamp.now()
      });
      
      console.log('✅ Test reminder marked as completed');
      
      // Wait and check if next reminder was created
      setTimeout(async () => {
        const newRemindersQuery = query(
          collection(db, 'users', userId, 'reminderQueue'),
          where('discipleId', '==', discipleId),
          where('archived', '==', false),
          orderBy('createdAt', 'desc'),
          limit(1)
        );
        
        const newRemindersSnapshot = await getDocs(newRemindersQuery);
        if (!newRemindersSnapshot.empty) {
          const newReminder = newRemindersSnapshot.docs[0];
          console.log('✅ Next reminder was created:', {
            id: newReminder.id,
            scheduledFor: newReminder.data().scheduledFor?.toDate(),
            status: newReminder.data().reminderStatus
          });
        } else {
          console.log('❌ No next reminder was created');
        }
      }, 5000);
      
      return reminderRef.id;
      
    } catch (error) {
      console.error('❌ Error testing automatic reminder creation:', error);
      throw error;
    }
  }
};